import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { AUTH_PROVIDERS } from '@/consts/auth.consts';
import { useAuthConfig } from '@/hooks/useAuthConfig';

/**
 * Exposes whether the current app session is running inside Figma's embed iframe.
 *
 * Child components should call `useIsFigmaEmbedMode()` rather than checking
 * `useAuthConfig().provider === AUTH_PROVIDERS.FIGMA_EMBED` themselves — the provider here
 * centralizes that derivation so the "how we detect embed mode" logic stays in one place.
 */
const FigmaEmbedModeContext = createContext<boolean>(false);

export function FigmaEmbedModeProvider({ children }: { children: ReactNode }) {
  const { provider } = useAuthConfig();
  const isFigmaEmbedMode = useMemo(() => provider === AUTH_PROVIDERS.FIGMA_EMBED, [provider]);
  return <FigmaEmbedModeContext.Provider value={isFigmaEmbedMode}>{children}</FigmaEmbedModeContext.Provider>;
}

export function useIsFigmaEmbedMode(): boolean {
  return useContext(FigmaEmbedModeContext);
}
